import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { OPPORTUNITIES, MAP_PINS } from "../data/mockData";
import OpportunityCard from "../components/OpportunityCard";
import StaticMap from "../components/StaticMap";
import { SBLogo } from "../components/WebLayout";
import { Search, Map as MapIcon, LayoutList, ChevronDown, Bookmark, Bell, X } from "lucide-react";

const LOC_TYPES = ["Warehouse", "Manufacturing", "Office", "Hotel", "Apartment", "Gym", "Trucking/Logistics"];
const MACHINE_TYPES = ["Snack", "Drink", "Combo", "Smart Cooler", "Coffee"];

export default function Marketplace() {
  const [view, setView] = useState("list");
  const [radius, setRadius] = useState(50);
  const [minPeople, setMinPeople] = useState(50);
  const [priceMax, setPriceMax] = useState(1000);
  const [verifMin, setVerifMin] = useState(70);
  const [open247, setOpen247] = useState(false);
  const [multiShift, setMultiShift] = useState(false);
  const [locTypes, setLocTypes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [activePin, setActivePin] = useState(null);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const filtered = useMemo(() => {
    let r = OPPORTUNITIES.slice();
    if (query) r = r.filter((o) => `${o.city} ${o.state} ${o.zip}`.toLowerCase().includes(query.toLowerCase()));
    r = r.filter((o) => o.employees >= minPeople && o.price <= priceMax && o.verification >= verifMin);
    if (open247) r = r.filter((o) => o.open247);
    if (multiShift) r = r.filter((o) => o.shifts >= 2);
    if (sort === "price_asc") r.sort((a, b) => a.price - b.price);
    if (sort === "price_desc") r.sort((a, b) => b.price - a.price);
    if (sort === "verification") r.sort((a, b) => b.verification - a.verification);
    if (sort === "newest") r.sort((a, b) => a.listedDaysAgo - b.listedDaysAgo);
    return r;
  }, [query, minPeople, priceMax, verifMin, open247, multiShift, sort]);

  const activeOpp = activePin ? OPPORTUNITIES.find((o) => o.id === activePin) : null;

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#080B0D]/85 border-b border-[#2A2F34]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><SBLogo size={20} /></Link>
          <div className="hidden md:flex items-center gap-4 text-[13px] text-[#A1A1AA]">
            <span className="text-white font-semibold">Marketplace</span>
            <Link to="/operator" className="hover:text-white">Dashboard</Link>
          </div>
          <button className="sb-pill sb-pill-gray"><Bell size={13} /> Alerts</button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8 grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="sb-card p-5 h-max lg:sticky lg:top-24">
          <div className="sb-eyebrow">Search</div>
          <div className="mt-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="City, ZIP…"
              data-testid="marketplace-search"
              className="sb-input pl-9"
            />
          </div>

          <div className="mt-5">
            <label className="sb-label">Radius: <span className="text-white">{radius} mi</span></label>
            <input type="range" min={5} max={200} value={radius} onChange={(e) => setRadius(+e.target.value)} className="w-full accent-[#FFC107]" />
          </div>

          <div className="mt-5">
            <div className="sb-label">Location Type</div>
            <div className="flex flex-wrap gap-2">
              {LOC_TYPES.map((t) => (
                <button key={t} onClick={() => toggle(locTypes, setLocTypes, t)} className={`sb-pill ${locTypes.includes(t) ? "sb-pill-gold" : "sb-pill-gray"}`}>{t}</button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <div className="sb-label">Machine Type</div>
            <div className="flex flex-wrap gap-2">
              {MACHINE_TYPES.map((t) => (
                <button key={t} onClick={() => toggle(machines, setMachines, t)} className={`sb-pill ${machines.includes(t) ? "sb-pill-gold" : "sb-pill-gray"}`}>{t}</button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <label className="sb-label">Min. People: <span className="text-white">{minPeople}+</span></label>
            <input type="range" min={0} max={500} step={10} value={minPeople} onChange={(e) => setMinPeople(+e.target.value)} className="w-full accent-[#FFC107]" />
          </div>

          <div className="mt-5">
            <label className="sb-label">Price up to: <span className="text-white">${priceMax}</span></label>
            <input type="range" min={100} max={1200} step={50} value={priceMax} onChange={(e) => setPriceMax(+e.target.value)} className="w-full accent-[#FFC107]" />
          </div>

          <div className="mt-5">
            <label className="sb-label">Verification ≥ <span className="text-white">{verifMin}</span></label>
            <input type="range" min={0} max={100} value={verifMin} onChange={(e) => setVerifMin(+e.target.value)} className="w-full accent-[#FFC107]" />
          </div>

          <label className="mt-5 flex items-center justify-between text-[13px]">
            <span>Open 24/7</span>
            <input type="checkbox" checked={open247} onChange={(e) => setOpen247(e.target.checked)} />
          </label>
          <label className="mt-3 flex items-center justify-between text-[13px]">
            <span>Multiple Shifts</span>
            <input type="checkbox" checked={multiShift} onChange={(e) => setMultiShift(e.target.checked)} />
          </label>

          <button className="mt-6 sb-btn-secondary w-full text-[11px]">
            <Bookmark size={13} /> Save Search / Save Territory
          </button>
        </aside>

        {/* Main */}
        <div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="sb-eyebrow">Opportunities</div>
              <div className="text-[22px] font-black mt-1">
                {filtered.length} verified spots available
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="sb-card p-1 flex text-[12px]">
                <button onClick={() => setView("map")} data-testid="view-map" className={`px-3 py-1.5 rounded-md flex items-center gap-1.5 ${view === "map" ? "bg-[#FFC107] text-[#080B0D] font-bold" : "text-[#A1A1AA]"}`}><MapIcon size={13} /> Map</button>
                <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 py-1.5 rounded-md flex items-center gap-1.5 ${view === "list" ? "bg-[#FFC107] text-[#080B0D] font-bold" : "text-[#A1A1AA]"}`}><LayoutList size={13} /> List</button>
              </div>
              <div className="relative">
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="sb-input pr-8 text-[12px]" style={{ padding: "8px 30px 8px 12px" }}>
                  <option value="newest">Newest</option>
                  <option value="price_asc">Price: Low → High</option>
                  <option value="price_desc">Price: High → Low</option>
                  <option value="verification">Verification: High</option>
                </select>
                <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#52525b] pointer-events-none" />
              </div>
            </div>
          </div>

          {view === "map" ? (
            <div className="mt-6">
              <StaticMap
                pins={MAP_PINS}
                height={520}
                activeId={activePin}
                onPinClick={setActivePin}
                mode="approximate"
                showLegend
                overlay={activeOpp && (
                  <div className="absolute bottom-4 left-4 right-4 md:left-6 md:right-auto md:w-[360px] sb-card p-4 z-20">
                    <button onClick={() => setActivePin(null)} className="absolute top-3 right-3 text-[#A1A1AA] hover:text-white"><X size={14} /></button>
                    <div className="sb-pill sb-pill-gold" style={{ fontSize: 9 }}>Approximate Opportunity Area</div>
                    <div className="mt-3 text-[14px] font-black">{activeOpp.city}, {activeOpp.state} · {activeOpp.zip}</div>
                    <div className="mt-1 text-[11px] text-[#A1A1AA]">Exact address unlocks after purchase.</div>
                    <div className="mt-1 text-[12px] text-[#A1A1AA]">~{activeOpp.employees} people · {activeOpp.machines}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[18px] font-black">${activeOpp.price}</span>
                      <Link to={`/marketplace/${activeOpp.id}`} className="sb-btn-primary text-[10px]" style={{ padding: "8px 12px" }}>View</Link>
                    </div>
                  </div>
                )}
              />
            </div>
          ) : (
            <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((o) => <OpportunityCard key={o.id} opp={o} />)}
              {filtered.length === 0 && (
                <div className="col-span-full sb-card p-10 text-center text-[#A1A1AA]">No opportunities match those filters yet.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
